import React, { useState } from 'react';
import Button from 'components/ui/Button';
import { Download, FileText, Table, BarChart3, Calendar, Share2 } from 'lucide-react';

const ExportPanel = ({ trackingLinks, clickData, filters }) => {
  const [isExporting, setIsExporting] = useState(false);

  const generateCSVData = (data, type) => {
    if (type === 'links') {
      const headers = ['Titre', 'URL Courte', 'URL Cible', 'Clics Totaux', 'Clics Uniques', 'Statut', 'Source UTM', 'Medium UTM', 'Date Création'];
      const rows = data?.map(link => [
        link?.title || '',
        link?.short_url || '',
        link?.target_url || '',
        link?.total_clicks || 0,
        link?.unique_clicks || 0,
        link?.status || '',
        link?.utm_source || '',
        link?.utm_medium || '',
        new Date(link.created_at)?.toLocaleDateString('fr-FR')
      ]) || [];
      return [headers, ...rows];
    } else if (type === 'clicks') {
      const headers = ['Date', 'Source', 'Appareil', 'Navigateur', 'OS', 'Pays', 'Ville'];
      const rows = data?.map(click => [
        new Date(click.clicked_at)?.toLocaleDateString('fr-FR'),
        click?.source || '',
        click?.device_type || '',
        click?.browser || '',
        click?.os || '',
        click?.country || '',
        click?.city || ''
      ]) || [];
      return [headers, ...rows];
    }
    return [];
  };

  const downloadCSV = async (data, filename) => {
    setIsExporting(true);
    try {
      const csvContent = data?.map(row => 
        row?.map(cell => `"${cell}"`)?.join(',')
      )?.join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      if (link?.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link?.setAttribute('href', url);
        link?.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body?.appendChild(link);
        link?.click();
        document.body?.removeChild(link);
      }
    } catch (error) {
      console.error('Export error:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const exportLinks = () => {
    const csvData = generateCSVData(trackingLinks, 'links');
    const filename = `liens-tracking-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  const exportClicks = () => {
    const csvData = generateCSVData(clickData, 'clicks');
    const filename = `analytics-clics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    downloadCSV(csvData, filename);
  };

  const generateReport = () => {
    // Generate a summary report
    const totalClicks = trackingLinks?.reduce((sum, link) => sum + (link?.total_clicks || 0), 0) || 0;
    const totalUniqueClicks = trackingLinks?.reduce((sum, link) => sum + (link?.unique_clicks || 0), 0) || 0;
    const activeLinks = trackingLinks?.filter(link => link?.status === 'active')?.length || 0;
    
    const reportData = [
      ['Rapport d\'Analyse des Liens', ''],
      ['Date de génération', new Date()?.toLocaleDateString('fr-FR')],
      ['Période analysée', `${filters?.dateRange || 30} derniers jours`],
      ['', ''],
      ['RÉSUMÉ EXÉCUTIF', ''],
      ['Total des liens', trackingLinks?.length || 0],
      ['Liens actifs', activeLinks],
      ['Total des clics', totalClicks],
      ['Clics uniques', totalUniqueClicks],
      ['Taux d\'engagement moyen', totalClicks > 0 ? `${((totalUniqueClicks / totalClicks) * 100)?.toFixed(1)}%` : '0%'],
      ['', ''],
      ['TOP PERFORMERS', ''],
      ...((trackingLinks || [])?.sort((a, b) => (b?.total_clicks || 0) - (a?.total_clicks || 0))?.slice(0, 5)?.map((link, index) => [
          `${index + 1}. ${link?.title}`,
          `${link?.total_clicks || 0} clics`
        ]))
    ];
    
    const filename = `rapport-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    downloadCSV(reportData, filename);
  };

  const exportOptions = [
    {
      id: 'links',
      name: 'Données des Liens',
      description: 'Exporter les informations de tous vos liens de suivi',
      icon: Table,
      action: exportLinks,
      count: trackingLinks?.length || 0
    },
    {
      id: 'clicks',
      name: 'Données des Clics',
      description: 'Exporter le détail de tous les clics enregistrés',
      icon: BarChart3,
      action: exportClicks,
      count: clickData?.length || 0
    },
    {
      id: 'report',
      name: 'Rapport Complet',
      description: 'Générer un rapport de synthèse avec les KPIs principaux',
      icon: FileText,
      action: generateReport,
      count: null
    }
  ];

  const quickActions = [
    {
      name: 'Partager les résultats',
      description: 'Générer un lien de partage des statistiques',
      icon: Share2,
      action: () => {
        // This could generate a shareable dashboard link
        console.log('Share functionality to be implemented');
      }
    },
    {
      name: 'Rapport programmé',
      description: 'Configurer l\'envoi automatique de rapports',
      icon: Calendar,
      action: () => {
        console.log('Scheduled reports to be implemented');
      }
    }
  ];

  return (
    <div className="space-y-6">
      {/* Export Options */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <Download className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Exporter les Données</h3>
        </div>
        
        <div className="space-y-3">
          {exportOptions?.map((option) => {
            const IconComponent = option?.icon;
            return (
              <div
                key={option?.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <IconComponent className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-medium text-gray-900">
                      {option?.name}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {option?.description}
                    </p>
                    {option?.count !== null && (
                      <p className="text-xs text-indigo-600 mt-1">
                        {option?.count} élément(s)
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={option?.action}
                  loading={isExporting}
                  disabled={option?.count === 0}
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Actions Rapides
        </h3>
        
        <div className="space-y-3">
          {quickActions?.map((action, index) => {
            const IconComponent = action?.icon;
            return (
              <button
                key={index}
                onClick={action?.action}
                className="w-full flex items-center space-x-3 p-3 border border-gray-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
              >
                <div className="flex-shrink-0">
                  <IconComponent className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {action?.name}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">
                    {action?.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Summary Stats */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white">
        <h3 className="text-lg font-medium mb-4">Résumé</h3>
        
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm opacity-90">Liens créés</span>
            <span className="font-bold">{trackingLinks?.length || 0}</span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm opacity-90">Total des clics</span>
            <span className="font-bold">
              {trackingLinks?.reduce((sum, link) => sum + (link?.total_clicks || 0), 0) || 0}
            </span>
          </div>
          
          <div className="flex justify-between items-center">
            <span className="text-sm opacity-90">Clics uniques</span>
            <span className="font-bold">
              {trackingLinks?.reduce((sum, link) => sum + (link?.unique_clicks || 0), 0) || 0}
            </span>
          </div>
          
          <div className="pt-2 border-t border-white/20">
            <div className="text-xs opacity-75">
              Données pour les {filters?.dateRange || 30} derniers jours
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;